import React, { useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import { FileTextOutlined, UploadOutlined } from '@ant-design/icons';
import { unwrapCasPayload } from './casImportUtils';
import './JsonCasImportPanel.css';

function looksLikeCasJson(data) {
  if (!data || typeof data !== 'object') return false;
  return Boolean(
    data.demat_accounts
    || data.mutual_funds
    || data.investor
    || data.meta
    || data.nps
    || data.insurance
    || data.summary
    || data.parsedCas
  );
}

export default function JsonCasImportPanel({ isDark, onCasImported, embedded = false }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fileName, setFileName] = useState('');

  const importFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setError('');
    setSuccess('');
    setFileName(file.name);

    try {
      const text = await file.text();
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('That file is not valid JSON.');
      }

      const payload = unwrapCasPayload(parsed?.parsedCas || parsed);
      if (!looksLikeCasJson(payload)) {
        throw new Error('JSON does not look like a parsed CAS portfolio (missing demat/MF/investor fields).');
      }

      setSuccess(`Imported ${file.name}`);
      if (typeof onCasImported === 'function') {
        onCasImported(payload, {
          parser_type: 'JSON_FILE',
          fileName: file.name,
        });
      }
    } catch (err) {
      setError(err?.message || 'Failed to import JSON file.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <section className={`json-cas${isDark ? ' json-cas--dark' : ''}${embedded ? ' json-cas--embedded' : ''}`}>
      <div className="json-cas-panel">
        {!embedded && (
          <div className="json-cas-copy">
            <h2 className="json-cas-title">Import parsed CAS JSON</h2>
            <p className="json-cas-hint">
              Upload a previously parsed CAS JSON export (same shape as CASParser / imported-portfolio.json).
            </p>
          </div>
        )}

        <p className="json-cas-hint">
          Choose a <code>.json</code> file with demat holdings, mutual funds, and summary — useful for testing corporate bonds without a PDF.
        </p>

        <input
          ref={inputRef}
          type="file"
          accept="application/json,.json"
          className="json-cas-input"
          onChange={(e) => importFile(e.target.files?.[0])}
        />

        <div className="json-cas-actions">
          <button
            type="button"
            className="json-cas-btn"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? <Spin size="small" /> : <UploadOutlined />}
            Choose JSON file
          </button>
        </div>

        {fileName && !error && (
          <div className="json-cas-status">
            <FileTextOutlined /> {fileName}
          </div>
        )}

        {error && <Alert type="error" showIcon message={error} className="json-cas-alert" />}
        {success && !error && <Alert type="success" showIcon message={success} className="json-cas-alert" />}
      </div>
    </section>
  );
}
